import React, { useEffect, useState } from "react";
import { List, ListItem, ListItemDecorator, Radio, RadioGroup } from "@mui/joy";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { getFetch, postFetch } from "../../../../../fetch/fetch";

type BranchStatus = "full" | "soon" | "open";

// 공개 페이지(registerwaiting2026.tsx)의 BRANCHES 와 동일하게 유지
const BRANCHES: { id: string; name: string }[] = [
    { id: "daechi", name: "대치점" }, { id: "gangnam", name: "강남점" },
    { id: "songpa", name: "송파점" }, { id: "bundang", name: "분당점" },
    { id: "daegu", name: "수성점" }, { id: "gimpo", name: "김포점" },
    { id: "sinchon", name: "신촌점" }, { id: "gangdong", name: "강동점" },
    { id: "hanam", name: "하남점" }, { id: "centum", name: "센텀점" },
    { id: "dongtan", name: "동탄점" },
];

const STATUS_OPTIONS: { value: BranchStatus; label: string; color: "danger" | "warning" | "success"; icon: JSX.Element }[] = [
    { value: "full", label: "대기 등록", color: "danger", icon: <HourglassBottomRoundedIcon /> },
    { value: "soon", label: "마감 임박", color: "warning", icon: <WarningAmberRoundedIcon /> },
    { value: "open", label: "바로 등원", color: "success", icon: <CheckCircleRoundedIcon /> },
];

interface BranchDetail {
    status?: BranchStatus;
    teacherId?: number | null;
    teacherName?: string | null;
    updatedAt?: any;
}

const formatDateTime = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return typeof v === "string" ? v : "";
    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const BranchRecruitStatus: React.FC<any> = (props) => {

    const [detail, setDetail] = useState<{ [id: string]: BranchDetail }>({});

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const result: any = await getFetch("/waiting/branch-recruitment-status-admin");
            if (result.message === "success" && result.detail) {
                setDetail(result.detail);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleChange = async (location: string, status: BranchStatus) => {

        const prev = detail[location];

        // 낙관적 업데이트
        setDetail((d) => ({ ...d, [location]: { ...d[location], status } }));

        try {
            const result: any = await postFetch("/waiting/branch-recruitment-status-update", {
                data: { location, status }
            });

            if (result.message === "success") {
                setDetail((d) => ({
                    ...d,
                    [location]: {
                        status,
                        teacherId: result.teacherId,
                        teacherName: result.teacherName,
                        updatedAt: result.updatedAt,
                    }
                }));
            } else {
                // 권한 등 실패 → 롤백
                setDetail((d) => ({ ...d, [location]: prev }));
                alert(result.message === "NO_TODOIST" ? "권한이 없습니다 (todoistApiKey 필요)." : "저장에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            setDetail((d) => ({ ...d, [location]: prev }));
            alert("저장 중 오류가 발생했습니다.");
        }
    };

    return (
        <div style={{ width: "96%", paddingBottom: "80px" }}>
            <div style={{ fontFamily: "Apple_B", marginTop: "50px", marginBottom: "8px", fontSize: "18px" }}>
                각 지점별 모집상태
            </div>
            <div style={{ fontFamily: "Apple_R", marginBottom: "24px", fontSize: "13px", color: "#6b7280" }}>
                선택하면 즉시 저장되고, 수능선배 대기등록 페이지(registerWaiting2026)에 반영됩니다.
            </div>

            {BRANCHES.map((b) => {
                const d = detail[b.id] || {};
                return (
                    <div key={b.id} style={{
                        display: "flex", alignItems: "center", flexWrap: "wrap",
                        padding: "12px 20px", marginBottom: "12px",
                        border: "1px solid #e5e7eb", borderRadius: "12px", boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                    }}>
                        <div style={{ fontFamily: "Apple_B", fontSize: "16px", width: "90px", flexShrink: 0 }}>
                            {b.name}
                        </div>

                        <RadioGroup
                            orientation="horizontal"
                            aria-label={`${b.name} 모집상태`}
                            name={`branch-${b.id}`}
                            value={d.status || ""}
                            onChange={(e: any) => handleChange(b.id, e.target.value as BranchStatus)}
                        >
                            <List
                                orientation="horizontal"
                                sx={{
                                    '--List-gap': '0.5rem',
                                    '--ListItem-paddingY': '0.6rem',
                                    '--ListItem-radius': '8px',
                                    '--ListItemDecorator-size': '32px',
                                }}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <ListItem variant="outlined" key={opt.value} sx={{ boxShadow: 'sm', minWidth: 150 }}>
                                        <ListItemDecorator>
                                            {opt.icon}
                                        </ListItemDecorator>
                                        <Radio
                                            overlay
                                            value={opt.value}
                                            label={opt.label}
                                            color={opt.color}
                                            sx={{ flexGrow: 1, flexDirection: 'row-reverse' }}
                                            slotProps={{
                                                action: ({ checked }: any) => ({
                                                    sx: (theme: any) => ({
                                                        ...(checked && {
                                                            inset: -1,
                                                            border: '2px solid',
                                                            borderColor: theme.vars.palette[opt.color][500],
                                                        }),
                                                    }),
                                                }),
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </RadioGroup>

                        <div style={{
                            fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af",
                            marginLeft: "20px", flexShrink: 0
                        }}>
                            마지막 수정: {d.teacherName ? d.teacherName : "—"}
                            {d.updatedAt ? ` · ${formatDateTime(d.updatedAt)}` : ""}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default BranchRecruitStatus;
